<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h4 class="card-title mb-0">API凭证详情</h4>
              <el-button @click="$router.go(-1)">
                <i class="el-icon-back"></i> 返回
              </el-button>
            </div>

            <el-tabs v-model="activeTab" @tab-click="handleTabClick">
              <!-- 基本信息 -->
              <el-tab-pane label="基本信息" name="info">
                <div v-loading="loading" class="mt-3">
                  <el-descriptions :column="2" border v-if="credential">
                    <el-descriptions-item label="名称">
                      {{ credential.name }}
                    </el-descriptions-item>
                    <el-descriptions-item label="状态">
                      <el-tag :type="credential.status === 1 ? 'success' : 'danger'" size="small">
                        {{ credential.status === 1 ? '启用' : '禁用' }}
                      </el-tag>
                    </el-descriptions-item>
                    <el-descriptions-item label="API Key">
                      <span class="text-muted">{{ credential.api_key }}</span>
                      <el-button
                        type="text"
                        size="mini"
                        icon="el-icon-copy-document"
                        @click="copyToClipboard(credential.api_key)"
                        style="margin-left: 10px;"
                      >
                        复制
                      </el-button>
                    </el-descriptions-item>
                    <el-descriptions-item label="创建时间">
                      {{ credential.created_at }}
                    </el-descriptions-item>
                    <el-descriptions-item label="最后使用">
                      {{ credential.last_used_at || '从未使用' }}
                    </el-descriptions-item>
                    <el-descriptions-item label="过期时间">
                      {{ credential.expires_at || '永不过期' }}
                    </el-descriptions-item>
                    <el-descriptions-item label="描述" :span="2">
                      {{ credential.description || '无' }}
                    </el-descriptions-item>
                    <el-descriptions-item label="IP白名单" :span="2">
                      {{ credential.ip_whitelist || '无限制' }}
                    </el-descriptions-item>
                    <el-descriptions-item label="速率限制">
                      {{ credential.rate_limit }} 次/分钟
                    </el-descriptions-item>
                    <el-descriptions-item label="统计信息">
                      <div>
                        <span>总调用: {{ credential.statistics?.total_calls || 0 }}</span>
                        <span class="ml-3">最近24小时: {{ credential.statistics?.recent_24h_calls || 0 }}</span>
                      </div>
                    </el-descriptions-item>
                  </el-descriptions>
                </div>
              </el-tab-pane>

              <!-- 调用日志 -->
              <el-tab-pane label="调用日志" name="logs">
                <div class="mt-3">
                  <!-- 筛选器 -->
                  <el-form :inline="true" class="mb-3">
                    <el-form-item label="状态码">
                      <el-select v-model="logFilters.status_code" placeholder="全部" clearable style="width: 150px">
                        <el-option label="200" value="200" />
                        <el-option label="400" value="400" />
                        <el-option label="401" value="401" />
                        <el-option label="500" value="500" />
                      </el-select>
                    </el-form-item>
                    <el-form-item label="开始时间">
                      <el-date-picker
                        v-model="logFilters.start_time"
                        type="datetime"
                        placeholder="选择开始时间"
                        format="yyyy-MM-dd HH:mm:ss"
                        value-format="timestamp"
                        style="width: 200px"
                      />
                    </el-form-item>
                    <el-form-item label="结束时间">
                      <el-date-picker
                        v-model="logFilters.end_time"
                        type="datetime"
                        placeholder="选择结束时间"
                        format="yyyy-MM-dd HH:mm:ss"
                        value-format="timestamp"
                        style="width: 200px"
                      />
                    </el-form-item>
                    <el-form-item>
                      <el-button type="primary" @click="loadLogs">查询</el-button>
                      <el-button @click="resetLogFilters">重置</el-button>
                    </el-form-item>
                  </el-form>

                  <!-- 日志表格 -->
                  <el-table
                    v-loading="logsLoading"
                    :data="logs"
                    style="width: 100%"
                    border
                  >
                    <el-table-column prop="request_time" label="请求时间" width="180" />
                    <el-table-column prop="endpoint" label="端点" min-width="200" />
                    <el-table-column prop="method" label="方法" width="80" />
                    <el-table-column prop="status_code" label="状态码" width="100" align="center">
                      <template slot-scope="scope">
                        <el-tag
                          :type="scope.row.status_code >= 200 && scope.row.status_code < 300 ? 'success' : 
                                 scope.row.status_code >= 400 && scope.row.status_code < 500 ? 'warning' : 'danger'"
                          size="small"
                        >
                          {{ scope.row.status_code }}
                        </el-tag>
                      </template>
                    </el-table-column>
                    <el-table-column prop="request_ip" label="IP地址" width="150" />
                    <el-table-column prop="response_time" label="响应时间" width="100" align="right">
                      <template slot-scope="scope">
                        {{ scope.row.response_time ? scope.row.response_time + 'ms' : '-' }}
                      </template>
                    </el-table-column>
                    <el-table-column prop="error_message" label="错误信息" min-width="200">
                      <template slot-scope="scope">
                        <span v-if="scope.row.error_message" class="text-danger">
                          {{ scope.row.error_message }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </template>
                    </el-table-column>
                  </el-table>

                  <!-- 分页 -->
                  <div class="mt-3 text-right">
                    <el-pagination
                      @size-change="handleLogSizeChange"
                      @current-change="handleLogPageChange"
                      :current-page="logPagination.page"
                      :page-sizes="[10, 25, 50, 100]"
                      :page-size="logPagination.per_page"
                      layout="total, sizes, prev, pager, next, jumper"
                      :total="logPagination.total"
                    />
                  </div>
                </div>
              </el-tab-pane>

              <!-- 统计信息 -->
              <el-tab-pane label="统计信息" name="statistics">
                <div v-loading="statisticsLoading" class="mt-3">
                  <div v-if="statistics" class="row">
                    <div class="col-md-6 mb-3">
                      <div class="card">
                        <div class="card-body">
                          <h5 class="card-title">调用统计</h5>
                          <p class="mb-1">总调用次数: <strong>{{ statistics.total_calls }}</strong></p>
                          <p class="mb-1">最近30天: <strong>{{ statistics.recent_30d_calls }}</strong></p>
                          <p class="mb-0">平均响应时间: <strong>{{ statistics.avg_response_time_ms || 0 }}ms</strong></p>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6 mb-3">
                      <div class="card">
                        <div class="card-body">
                          <h5 class="card-title">状态码分布</h5>
                          <div v-for="(count, code) in statistics.status_code_stats" :key="code" class="mb-1">
                            <span>{{ code }}: </span>
                            <el-progress :percentage="(count / statistics.recent_30d_calls * 100).toFixed(1)" :format="() => count" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-12 mb-3">
                      <div class="card">
                        <div class="card-body">
                          <h5 class="card-title">Top 10 端点</h5>
                          <el-table :data="statistics.top_endpoints" style="width: 100%" border>
                            <el-table-column prop="endpoint" label="端点" />
                            <el-table-column prop="count" label="调用次数" width="150" align="right" />
                          </el-table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </el-tab-pane>
            </el-tabs>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { authMixin, deviceMixin } from "@/mixins";
import { apiCredentialService } from "@/services/api.service";
import { requireAuth } from "@/utils/auth";

export default {
  page() {
    return {
      title: "API凭证详情",
      meta: [{ name: "description" }],
    };
  },
  components: { Layout, PageHeader },
  mixins: [authMixin, deviceMixin],
  data() {
    return {
      title: "API凭证详情",
      items: [
        {
          text: "设置",
          href: "/",
        },
        {
          text: "API凭证管理",
          href: "/setting/apicredentials",
        },
        {
          text: "详情",
          active: true,
        },
      ],
      activeTab: "info",
      loading: false,
      credential: null,
      credentialId: null,
      logsLoading: false,
      logs: [],
      logFilters: {
        status_code: null,
        start_time: null,
        end_time: null,
      },
      logPagination: {
        page: 1,
        per_page: 25,
        total: 0,
      },
      statisticsLoading: false,
      statistics: null,
    };
  },
  mounted() {
    this.credentialId = this.$route.params.id;
    this.loadCredential();
  },
  methods: {
    /**
     * 加载凭证详情
     */
    async loadCredential() {
      if (!requireAuth(this)) {
        return;
      }

      this.loading = true;
      try {
        const response = await apiCredentialService.getCredential(this.credentialId, this);
        if (response && response.status === 200) {
          this.credential = response.data;
        }
      } catch (error) {
        console.error("加载凭证详情失败:", error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * 标签页切换
     */
    handleTabClick(tab) {
      if (tab.name === "logs") {
        this.loadLogs();
      } else if (tab.name === "statistics") {
        this.loadStatistics();
      }
    },

    /**
     * 加载调用日志
     */
    async loadLogs() {
      if (!requireAuth(this)) {
        return;
      }

      this.logsLoading = true;
      try {
        const params = {
          page: this.logPagination.page,
          per_page: this.logPagination.per_page,
        };

        if (this.logFilters.status_code) {
          params.status_code = parseInt(this.logFilters.status_code);
        }
        if (this.logFilters.start_time) {
          params.start_time = Math.floor(this.logFilters.start_time / 1000);
        }
        if (this.logFilters.end_time) {
          params.end_time = Math.floor(this.logFilters.end_time / 1000);
        }

        const response = await apiCredentialService.getCredentialLogs(
          this.credentialId,
          params,
          this
        );
        if (response && response.status === 200) {
          this.logs = response.data || [];
          if (response.pagination) {
            this.logPagination = {
              page: response.pagination.page,
              per_page: response.pagination.per_page,
              total: response.pagination.total,
            };
          }
        }
      } catch (error) {
        console.error("加载调用日志失败:", error);
      } finally {
        this.logsLoading = false;
      }
    },

    /**
     * 加载统计信息
     */
    async loadStatistics() {
      if (!requireAuth(this)) {
        return;
      }

      this.statisticsLoading = true;
      try {
        const response = await apiCredentialService.getCredentialStatistics(
          this.credentialId,
          this
        );
        if (response && response.status === 200) {
          this.statistics = response.data;
        }
      } catch (error) {
        console.error("加载统计信息失败:", error);
      } finally {
        this.statisticsLoading = false;
      }
    },

    /**
     * 重置日志筛选
     */
    resetLogFilters() {
      this.logFilters = {
        status_code: null,
        start_time: null,
        end_time: null,
      };
      this.logPagination.page = 1;
      this.loadLogs();
    },

    /**
     * 日志分页大小改变
     */
    handleLogSizeChange(size) {
      this.logPagination.per_page = size;
      this.logPagination.page = 1;
      this.loadLogs();
    },

    /**
     * 日志页码改变
     */
    handleLogPageChange(page) {
      this.logPagination.page = page;
      this.loadLogs();
    },

    /**
     * 复制到剪贴板
     */
    copyToClipboard(text) {
      if (!text) return;
      
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      
      try {
        document.execCommand("copy");
        this.$message({
          message: "已复制到剪贴板",
          type: "success",
        });
      } catch (err) {
        this.$message({
          message: "复制失败，请手动复制",
          type: "error",
        });
      } finally {
        document.body.removeChild(textarea);
      }
    },
  },
};
</script>

<style scoped>
.text-danger {
  color: #f56c6c;
}
.text-muted {
  color: #909399;
}
</style>

